/**
 * Logger Utility
 *
 * Centralized logging utility that respects environment settings.
 * In production, only errors are logged. In development, all logs are shown.
 *
 * Usage:
 * ```ts
 * import { logger } from '@/lib/utils/logger';
 *
 * logger.log('Info message');
 * logger.debug('Debug message');
 * logger.warn('Warning message');
 * logger.error('Error message');
 * ```
 */

const isDev = import.meta.env.DEV;

/**
 * Logger interface
 */
export interface Logger {
  /** Log info-level messages (development only) */
  info: (...args: unknown[]) => void;
  /** Log general information (development only) */
  log: (...args: unknown[]) => void;
  /** Log debug information (development only) */
  debug: (...args: unknown[]) => void;
  /** Log warnings (development only) */
  warn: (...args: unknown[]) => void;
  /** Log errors (always logged) */
  error: (...args: unknown[]) => void;
  /** Log with specific prefix */
  withPrefix: (prefix: string) => Logger;
}

/**
 * Create logger with optional prefix
 */
function createLogger(prefix?: string): Logger {
  const formatMessage = (...args: unknown[]): unknown[] => {
    if (prefix) {
      return [prefix, ...args];
    }
    return args;
  };

  return {
    log: (...args: unknown[]) => {
      if (isDev) {
        console.log(...formatMessage(...args));
      }
    },

    debug: (...args: unknown[]) => {
      if (isDev) {
        console.debug(...formatMessage(...args));
      }
    },

    warn: (...args: unknown[]) => {
      if (isDev) {
        console.warn(...formatMessage(...args));
      }
    },

    error: (...args: unknown[]) => {
      // Always log errors, even in production
      console.error(...formatMessage(...args));
    },

    withPrefix: (newPrefix: string) => {
      const combinedPrefix = prefix ? `${prefix} ${newPrefix}` : newPrefix;
      return createLogger(combinedPrefix);
    },

    info: (...args: unknown[]) => {
      if (isDev) {
        console.info(...formatMessage(...args));
      }
    },
  };
}

/**
 * Default logger instance
 */
export const logger = createLogger();

/**
 * Create component-specific logger
 *
 * Usage:
 * ```ts
 * const logger = createComponentLogger('AudioRecorder');
 * logger.log('Recording started'); // Output: [AudioRecorder] Recording started
 * ```
 */
export function createComponentLogger(componentName: string): Logger {
  return createLogger(`[${componentName}]`);
}

/**
 * Create module-specific logger
 *
 * Usage:
 * ```ts
 * const logger = createModuleLogger('WebSocket');
 * logger.log('Connected'); // Output: [WebSocket] Connected
 * ```
 */
export function createModuleLogger(moduleName: string): Logger {
  return createLogger(`[${moduleName}]`);
}
